/**
 * Compact View - Grid-based leaderboard optimized for iPad
 * Shows maximum sins without scrolling
 */

import { useState, useEffect } from 'react';
import { fetchLeaderboardStats, fetchSinRankings } from '../lib/api-client';
import { CompactSinCard } from '../components/CompactSinCard';
import type { LeaderboardStats, SinRanking } from '../types';

export function CompactView() {
  const [stats, setStats] = useState<LeaderboardStats | null>(null);
  const [rankings, setRankings] = useState<SinRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();

    // Auto-refresh every 5 minutes (300000ms)
    const interval = setInterval(() => {
      loadData();
    }, 300000);

    return () => clearInterval(interval);
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      const [statsResponse, rankingsResponse] = await Promise.all([
        fetchLeaderboardStats(),
        fetchSinRankings(),
      ]);

      if (statsResponse.success && rankingsResponse.success) {
        setStats(statsResponse.data);
        setRankings(rankingsResponse.data.rankings);
      } else {
        throw new Error('Failed to load leaderboard data');
      }
    } catch (err) {
      console.error('Error loading leaderboard:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load leaderboard data'
      );
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="app-container">
        <div className="matrix-bg"></div>
        <div className="scanlines"></div>
        <div className="glow-orb"></div>

        <div className="corner-bracket corner-tl"></div>
        <div className="corner-bracket corner-tr"></div>
        <div className="corner-bracket corner-bl"></div>
        <div className="corner-bracket corner-br"></div>

        <div className="loading">
          <div className="loading-icon-lg">⚖️</div>
          <div className="loading-title">FATHER GOVERNANCE</div>
          <div className="loading-text">
            &gt; ASSESSING_SINS // TALLYING_TRANSGRESSIONS...
          </div>
          <div className="loading-subtext">
            &gt; CALCULATING_PENANCE // PLEASE_WAIT...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <div className="matrix-bg"></div>
        <div className="scanlines"></div>
        <div className="glow-orb"></div>

        <div className="corner-bracket corner-tl"></div>
        <div className="corner-bracket corner-tr"></div>
        <div className="corner-bracket corner-bl"></div>
        <div className="corner-bracket corner-br"></div>

        <div className="error">
          <div className="error-box">
            <div className="error-icon">⚠️</div>
            <div className="error-text">{error}</div>
            <button onClick={loadData} className="retry-button">
              RETRY
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container compact-view">
      {/* Visual effects */}
      <div className="matrix-bg"></div>
      <div className="scanlines"></div>
      <div className="glow-orb"></div>

      {/* Corner brackets */}
      <div className="corner-bracket corner-tl"></div>
      <div className="corner-bracket corner-tr"></div>
      <div className="corner-bracket corner-bl"></div>
      <div className="corner-bracket corner-br"></div>

      {/* Compact Header */}
      <header className="compact-header">
        <h1 className="compact-title">
          SIN LEADERBOARD
        </h1>

        <p className="compact-subtitle">
          A sacred space for weary souls of DAO participants
        </p>

        {stats && (
          <>
            <div className="compact-stats-row">
              <div className="compact-stat">
                <span className="compact-stat-value">{stats.totalConfessions}</span>
                <span className="compact-stat-label">CONFESSIONS</span>
              </div>
              <div className="compact-stat">
                <span className="compact-stat-value">{stats.totalSins}</span>
                <span className="compact-stat-label">SINS</span>
              </div>
              <div className="compact-stat">
                <span className="compact-stat-value">{stats.totalConfessors}</span>
                <span className="compact-stat-label">CONFESSORS</span>
              </div>
            </div>

            <div className="compact-terminal">
              &gt; LAST_UPDATED // {new Date(stats.lastUpdated).toLocaleString()}
            </div>
          </>
        )}
      </header>

      {/* Grid Content */}
      <main className="compact-main">
        {rankings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📿</div>
            <div className="empty-title">No Confessions Yet</div>
          </div>
        ) : (
          <div className="compact-grid">
            {rankings.map((sin, index) => (
              <CompactSinCard key={sin.sinId} sin={sin} rank={index + 1} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
